/*
 * ygh-countdown.js - shared countdown used across the site (fixed date or nightly self-reset)
 * (announcement bar, sale banner, category pages, ...).
 *
 * Usage: give any element
 *   data-ygh-countdown data-end="2026-07-05T23:59:59"
 * with child spans [data-cd-d] [data-cd-h] [data-cd-m] [data-cd-s].
 *
 * - data-end is a WALL-CLOCK time in US Eastern (America/New_York),
 *   DST-aware via Intl (falls back to UTC-5 if Intl is unavailable).
 * - Counts DOWN to the fixed end date. Never resets. At 0 it stays at
 *   00:00:00:00 and the element gets an `is-ended` class.
 * - data-end="nightly" (or "nightly 00:30-04:30") = self-resetting daily
 *   timer: the deadline is a moment inside the night window (US Eastern),
 *   picked pseudo-randomly per calendar date (seeded by the date, so every
 *   visitor and both stores agree). When it passes, the next night's moment
 *   takes over, so the timer restarts on its own every night, weekends too.
 *   Window default 00:30-04:30 ET; optional data-seed salts the pick.
 * - All matching elements on the page are re-scanned every tick, so
 *   cloned markup (e.g. marquee announcement bars) stays in sync.
 * - Sale auto-bump: the worker at SALE_END_URL (ygh-sale-timer) answers
 *   { base, end }. Any fixed date equal to `base` (the date written in the
 *   theme files) counts down to `end` instead, so a running sale can be
 *   extended day by day without a theme change. window.__yghSaleEnd holds
 *   the current pair; "ygh:sale-end" fires on document when it arrives.
 */
(function () {
  if (window.__yghCountdown) return;
  window.__yghCountdown = true;

  var cache = {};

  // ---- sale end override (ygh-sale-timer worker) ------------------------
  var SALE_END_URL = 'https://raitislo.com/YGH/sale-timer/end';
  var saleEnd = null; // { base: 'YYYY-MM-DDTHH:MM:SS', end: 'YYYY-MM-DDTHH:MM:SS' }
  function applySaleEnd(o) {
    if (!o || !o.base || !o.end || o.base === o.end) return;
    saleEnd = { base: o.base, end: o.end };
    window.__yghSaleEnd = saleEnd;
    try { document.dispatchEvent(new CustomEvent('ygh:sale-end', { detail: saleEnd })); } catch (e) {}
  }
  function resolveEnd(spec) { return (saleEnd && spec === saleEnd.base) ? saleEnd.end : spec; }
  window.yghResolveSaleEnd = resolveEnd;
  try { // last known pair first (no flash of the old date), then refresh
    var c = JSON.parse(localStorage.getItem('ygh_sale_end') || 'null');
    if (c && c.ts && Date.now() - c.ts < 6 * 3600 * 1000) applySaleEnd(c);
  } catch (e) {}
  try {
    fetch(SALE_END_URL, { mode: 'cors', credentials: 'omit' }).then(function (r) { return r.ok ? r.json() : null; }).then(function (o) {
      if (!o) return;
      applySaleEnd(o);
      try { localStorage.setItem('ygh_sale_end', JSON.stringify({ base: o.base, end: o.end, ts: Date.now() })); } catch (e) {}
    }).catch(function () {});
  } catch (e) {}

  function etToUtc(str) {
    if (!str) return NaN;
    if (cache[str] !== undefined) return cache[str];
    var m = str.trim().replace(' ', 'T').split(/[-T:]/);
    var y = +m[0], mo = +m[1] || 1, d = +m[2] || 1, h = +m[3] || 0, mi = +m[4] || 0, s = +m[5] || 0;
    if (!y) { cache[str] = NaN; return NaN; }
    var wall = Date.UTC(y, mo - 1, d, h, mi, s);
    var ts = wall + 5 * 3600 * 1000; // fallback: treat input as UTC-5
    try {
      var fmt = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
      });
      ts = wall;
      for (var i = 0; i < 2; i++) { // converge on the UTC instant whose ET wall time matches
        var p = {};
        fmt.formatToParts(new Date(ts)).forEach(function (x) { p[x.type] = x.value; });
        var asET = Date.UTC(+p.year, +p.month - 1, +p.day, (+p.hour) % 24, +p.minute, +p.second);
        ts += wall - asET;
      }
    } catch (e) { /* keep UTC-5 fallback */ }
    cache[str] = ts;
    return ts;
  }

  function pad(n) { return String(Math.max(0, n)).padStart(2, '0'); }

  function setAll(el, sel, val) {
    var nodes = el.querySelectorAll(sel);
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].textContent !== val) nodes[i].textContent = val;
    }
  }

  // ---- nightly self-reset -------------------------------------------------
  var etFmt = null;
  function etDate(ts) { // calendar date in US Eastern for a UTC instant -> [y, m, d]
    try {
      if (!etFmt) etFmt = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' });
      var p = {};
      etFmt.formatToParts(new Date(ts)).forEach(function (x) { p[x.type] = x.value; });
      return [+p.year, +p.month, +p.day];
    } catch (e) {
      var d = new Date(ts - 5 * 3600 * 1000);
      return [d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate()];
    }
  }
  function hash(str) { // small deterministic string hash (FNV-1a)
    var h = 2166136261;
    for (var i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 16777619) >>> 0; }
    return h >>> 0;
  }
  function parseWindow(spec) { // "nightly 00:30-04:30" -> [30, 270] minutes after midnight
    var m = /(\d{1,2}):(\d{2})\s*-\s*(\d{1,2}):(\d{2})/.exec(spec || '');
    var a = m ? (+m[1]) * 60 + (+m[2]) : 30, b = m ? (+m[3]) * 60 + (+m[4]) : 270;
    if (b <= a) b = a + 1;
    return [a, b];
  }
  function nightlyMoment(ymd, win, seed) { // the reset moment on calendar date ymd (ET), as a UTC instant
    var key = ymd[0] + '-' + pad(ymd[1]) + '-' + pad(ymd[2]);
    var mins = win[0] + (hash(key + '|' + (seed || '')) % (win[1] - win[0]));
    return etToUtc(key + 'T' + pad(Math.floor(mins / 60)) + ':' + pad(mins % 60) + ':00');
  }
  var nightlyCache = {};
  function nightlyEnd(el, now) {
    var spec = el.getAttribute('data-end'), seed = el.getAttribute('data-seed') || '';
    var ck = spec + '|' + seed;
    var c = nightlyCache[ck];
    if (c && c > now) return c;
    var win = parseWindow(spec);
    var ymd = etDate(now), end = nightlyMoment(ymd, win, seed);
    for (var k = 1; end <= now && k <= 3; k++) { // tonight's moment already passed -> the next night
      end = nightlyMoment(etDate(Date.UTC(ymd[0], ymd[1] - 1, ymd[2] + k, 12)), win, seed);
    }
    nightlyCache[ck] = end;
    return end;
  }
  window.yghCountdownNightlyEnd = function (spec, seed) { // for debugging / QA in the console
    var el = document.createElement('div'); el.setAttribute('data-end', spec || 'nightly'); if (seed) el.setAttribute('data-seed', seed);
    return new Date(nightlyEnd(el, Date.now()));
  };

  function tick() {
    var els = document.querySelectorAll('[data-ygh-countdown]');
    if (!els.length) return;
    var now = Date.now();
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      var spec = resolveEnd(el.getAttribute('data-end') || '');
      var end = /^\s*nightly/i.test(spec) ? nightlyEnd(el, now) : etToUtc(spec);
      if (isNaN(end)) continue;
      var diff = end - now;
      var ended = diff <= 0;
      if (ended) diff = 0;
      if (el.classList.contains('is-ended') !== ended) el.classList.toggle('is-ended', ended);
      setAll(el, '[data-cd-d]', pad(Math.floor(diff / 86400000)));
      setAll(el, '[data-cd-h]', pad(Math.floor(diff % 86400000 / 3600000)));
      setAll(el, '[data-cd-m]', pad(Math.floor(diff % 3600000 / 60000)));
      setAll(el, '[data-cd-s]', pad(Math.floor(diff % 60000 / 1000)));
    }
  }

  tick();
  setInterval(tick, 1000);
})();
